import React, { useEffect } from 'react';
import { Bar, BarChart, Tooltip, XAxis, YAxis } from 'recharts';

import { AccountGroup, GUID, MonthlyAmount } from '@ndvr/portfolio-api';

import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { formatCurrency } from 'utils';
import { useGetMonthlyCashFlow } from 'views/portfolio/account-queries';

interface LiquidityPlanTimelineChartProps {
    accountGroupGUID: GUID<AccountGroup>;
    isDraftAccountGroup?: boolean;
}

interface ChartDataPoint {
    year: number;
    month?: number;
    contributions: number;
    withdrawals: number;
    displayLabel: string;
}

export const LiquidityPlanTimelineChart: React.FC<LiquidityPlanTimelineChartProps> = ({
    accountGroupGUID,
    isDraftAccountGroup,
}) => {
    const [viewMode, setViewMode] = React.useState<'monthly' | 'annual'>('annual');
    const [includeDrafts, setIncludeDrafts] = React.useState<boolean>(isDraftAccountGroup || false);

    const { data, isPending } = useGetMonthlyCashFlow(accountGroupGUID, includeDrafts);

    useEffect(() => {
        setIncludeDrafts(isDraftAccountGroup || false);
    }, [isDraftAccountGroup]);

    const chartData = React.useMemo(() => {
        if (!data) {
            return [];
        }

        const aggregatedData = new Map<string, ChartDataPoint>();

        const processMonthlyAmounts = (monthlyAmounts: MonthlyAmount[], field: 'contributions' | 'withdrawals') => {
            monthlyAmounts.forEach((monthlyAmount) => {
                const itemDate = new Date(monthlyAmount.yearMonth);

                const year = itemDate.getUTCFullYear();
                const month = itemDate.getUTCMonth();
                const key = viewMode === 'monthly' ? `${year}-${month}` : `${year}`;
                const displayLabel =
                    viewMode === 'monthly'
                        ? itemDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
                        : `${year}`;

                // Initialize or get existing data point for this period
                if (!aggregatedData.has(key)) {
                    aggregatedData.set(key, {
                        year,
                        month: viewMode === 'monthly' ? month : undefined,
                        contributions: 0,
                        withdrawals: 0,
                        displayLabel,
                    });
                }

                const dataPoint = aggregatedData.get(key)!;
                const amount = parseFloat(monthlyAmount.amount || '0');

                if (field === 'contributions') {
                    dataPoint.contributions += amount;
                } else {
                    // Make withdrawals negative for display below zero
                    dataPoint.withdrawals += -Math.abs(amount);
                }
            });
        };

        processMonthlyAmounts(data.contributions, 'contributions');
        processMonthlyAmounts(data.netWithdrawals, 'withdrawals');

        const sortedResult = Array.from(aggregatedData.values()).sort((a, b) => {
            if (a.year !== b.year) return a.year - b.year;
            return (a.month || 0) - (b.month || 0);
        });

        if (sortedResult.length === 0) {
            return [];
        }

        // Fill in missing periods to create continuous timeline
        const filledResult: ChartDataPoint[] = [];
        const firstPoint = sortedResult[0];
        const lastPoint = sortedResult[sortedResult.length - 1];

        if (viewMode === 'monthly') {
            // Create continuous monthly timeline
            const startDate = new Date(Date.UTC(firstPoint.year, firstPoint.month || 0, 1));
            const endDate = new Date(Date.UTC(lastPoint.year, lastPoint.month || 0, 1));

            // Limit to 24 months from start
            const maxDate = new Date(startDate);
            maxDate.setUTCMonth(maxDate.getUTCMonth() + 24);
            const limitedEndDate = endDate > maxDate ? maxDate : endDate;

            const current = new Date(startDate);
            while (current <= limitedEndDate) {
                const year = current.getUTCFullYear();
                const month = current.getUTCMonth();

                const existing = sortedResult.find((p) => p.year === year && p.month === month);
                if (existing) {
                    filledResult.push(existing);
                } else {
                    filledResult.push({
                        year,
                        month,
                        contributions: 0,
                        withdrawals: 0,
                        displayLabel: current.toLocaleDateString('en-US', {
                            month: 'short',
                            year: 'numeric',
                            timeZone: 'UTC',
                        }),
                    });
                }

                current.setUTCMonth(current.getUTCMonth() + 1);
            }
        } else {
            // Create continuous yearly timeline (show all years)
            for (let year = firstPoint.year; year <= lastPoint.year; year++) {
                const existing = sortedResult.find((p) => p.year === year);
                if (existing) {
                    filledResult.push(existing);
                } else {
                    filledResult.push({
                        year,
                        month: undefined,
                        contributions: 0,
                        withdrawals: 0,
                        displayLabel: `${year}`,
                    });
                }
            }
        }

        return filledResult;
    }, [data, viewMode]);

    interface CustomTooltipProps {
        active?: boolean;
        payload?: Array<{
            value: number;
            dataKey: string;
            color: string;
        }>;
        label?: string;
    }

    const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
        if (active && payload && payload.length) {
            return (
                <div className="rounded-lg border bg-white p-3 shadow-lg">
                    <p className="text-sm font-semibold">{label}</p>
                    {payload.map((entry: any) => {
                        if (entry.value !== 0) {
                            const displayValue = Math.abs(entry.value);
                            const entryLabel = entry.dataKey === 'contributions' ? 'Contributions' : 'Withdrawals';
                            return (
                                <p key={entry.dataKey} className="text-sm" style={{ color: entry.color }}>
                                    {`${entryLabel}: ${formatCurrency(displayValue)}`}
                                </p>
                            );
                        }
                        return null;
                    })}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="w-full">
            <div className="mb-4 flex items-center justify-between">
                <h2 className="text-cocoa-1000 text-xl">Liquidity Plan Timeline</h2>
                <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                        <Switch id="include-drafts" checked={includeDrafts} onCheckedChange={setIncludeDrafts} />
                        <Label htmlFor="include-drafts" className="text-sm">
                            Include Draft accounts
                        </Label>
                    </div>
                    <div className="flex items-center gap-1 rounded-lg border p-1">
                        <Button
                            variant={viewMode === 'monthly' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setViewMode('monthly')}
                            className="h-8 px-3 text-xs"
                        >
                            Monthly
                        </Button>
                        <Button
                            variant={viewMode === 'annual' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setViewMode('annual')}
                            className="h-8 px-3 text-xs"
                            style={viewMode === 'annual' ? { backgroundColor: 'rgb(125, 85, 43)', color: 'white' } : {}}
                        >
                            Annual
                        </Button>
                    </div>
                </div>
            </div>

            {!data || chartData.length === 0 ? (
                isPending ? (
                    <Spinner />
                ) : (
                    <div className="flex h-96 items-center justify-center text-gray-500">
                        No cash flow data available
                    </div>
                )
            ) : (
                <>
                    <div className="flex items-center justify-center gap-4">
                        <div className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded bg-[#b6e3c0]"></div>
                            <span className="text-sm text-gray-600">Contributions</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded bg-[#c5928a]"></div>
                            <span className="text-sm text-gray-600">Withdrawals</span>
                        </div>
                    </div>

                    <div className="h-96 w-full">
                        <BarChart
                            responsive
                            style={{ width: '100%', height: '100%' }}
                            className="[&_*]:!outline-none"
                            data={chartData}
                            stackOffset="sign"
                            maxBarSize={30}
                            margin={{
                                top: 20,
                                right: 30,
                                left: 40,
                                bottom: 40,
                            }}
                        >
                            <XAxis
                                dataKey="displayLabel"
                                tick={{ fontSize: 12 }}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                            />
                            <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => formatCurrency(Math.abs(value))} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="contributions" stackId="cashFlow" fill="#b6e3c0" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="withdrawals" stackId="cashFlow" fill="#c5928a" radius={[0, 0, 2, 2]} />
                        </BarChart>
                    </div>
                </>
            )}
        </div>
    );
};
